import { useQuery } from '@apollo/react-hooks';
import { useContext, useEffect } from 'react';
import gql from 'graphql-tag';

import {
	LIVE_PAGE_SESSION_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary';

// typsy type generation currently does not support queries from ClientSchema such as this one
import { ClientNetworkStatus } from './__types__/useLivePageConnectionListenerQuery';

export type useLivePageConnectionListenerProps = {
	collabEditConnectionTimedOut: boolean;
	collabInitTimedOut: boolean;
};

const useLivePageConnectionListenerQuery = gql`
	query useLivePageConnectionListenerQuery {
		network {
			status
		}
	}
`;

export const useLivePageConnectionListener = ({
	collabEditConnectionTimedOut,
	collabInitTimedOut,
}: useLivePageConnectionListenerProps) => {
	const isLivePage = useIsCurrentPageLive();

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, error } = useQuery(useLivePageConnectionListenerQuery, {
		fetchPolicy: 'cache-only',
		skip: !fg('confluence_fail_live_page_exp_on_ncs_disconnect'),
	});
	const status = data?.network?.status;

	useEffect(() => {
		if (error) {
			markErrorAsHandled(error);
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_MODES,
			});
		}
	}, [error]);

	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (!fg('confluence_fail_live_page_exp_on_ncs_disconnect')) {
			return;
		}

		const networkOffline =
			status === ClientNetworkStatus.IP_BLOCKED || status === ClientNetworkStatus.OFFLINE;
		const isCollabDisconnected = collabEditConnectionTimedOut || collabInitTimedOut;

		if (isLivePage && !networkOffline && isCollabDisconnected) {
			// If NCS times out, but user is still connected to network
			experienceTracker.fail({
				name: LIVE_PAGE_SESSION_EXPERIENCE,
				error: new Error('Collab edit provider timed out while network active'),
				attributes: {
					collabEditConnectionTimedOut,
					collabInitTimedOut,
					confluence_frontend_transition_preloading_system: fg(
						'confluence_frontend_transition_preloading_system',
					),
				},
			});
		}
	}, [status, collabEditConnectionTimedOut, isLivePage, experienceTracker, collabInitTimedOut]);
};
